import React, { memo, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { ageStyles } from "../../components/shared/styles";
import LibraryDialog from "../../components/shared/libraryDialog";
import TimePicker from "../../components/shared/timePicker";
import { DatePickerDialog } from '../../components/shared/datePicker';
import PracticeContentPreview from "../../components/PracticeContentPreview";
import { PracticeCardItem } from "../practice_templates/index";
import * as dayjs from 'dayjs';


import { teamsList, getTeamGroups } from '../../services/teams';
import { createPractice} from '../../services/practices';


const GroupPracticePlaning = () => {
  let location = useLocation();
  const navigate = useNavigate();

  const { method } = useParams();

  const { date } = location?.state || { date: new Date() };
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [practice, setPractice] = useState({ age: 10, duration: 75, source_method: method, scheduled_at: date })
  const [practiceTemplate, setPracticeTemplate] = useState(null)
  const [practiceDraft, setPracticeDraft] = useState({ exercises_practices_attributes: [] })
  const [practices, setPractices] = useState([])

  const { t } = useTranslation();


  const getMyTeams = async () => {
    const { teams } = await teamsList();
    teams.sort((a, b) => (a.name > b.name) - (a.name < b.name))
    setTeams(teams);
  };

  const fetchGroups = async () => {
    const { groups } = await getTeamGroups();
    groups.sort((a, b) => a.position - b.position);

    setGroups(groups);
  };

  useEffect(() => {
    fetchGroups();
    getMyTeams();
  }, []);

  useEffect(() => {
    if (step === 3) setSearchParams({...searchParams, from_age: practice?.age, to_age: practice?.age });

    if (step !== 5) { return; }

    // Prepare Schedule
    let list = [];
    teams.filter(team => selectedTeamIds.includes(team.id)).forEach(team => {
      let teamPractice = { team, team_id: team.id, ...practice }

      if (practice.source_method === 'manual') {
        teamPractice['exercises_practices_attributes'] = practiceDraft.exercises_practices_attributes;
      } else if (practice.source_method === 'from_template') {
        teamPractice['practice_template_id'] = practiceTemplate.id;
      }
      list.push(teamPractice);
    })
    setPractices(list);

  }, [step]);

  const next = () => {
    if (step === 3 && practice.source_method === 'generate') { setStep(5); return; }
    if (step === 1 && practice.source_method === 'generate') { setStep(3); return; }
    if (step === 4 && practice.source_method === 'from_template' && !practiceTemplate) { return; }
    if (step === 4 && practice.source_method === 'manual' && !practiceDraft?.exercises_practices_attributes?.length) { return; }

    setStep(step + 1);
  }

  const back = () => {
    if (step === 5 && practice.source_method === 'generate') { setStep(3); return; }
    if (step === 3 && practice.source_method === 'generate') { setStep(1); return; }

    setStep(step - 1);
  }

  return (
    <div className="teams form-container">
      <div className="teams-header">
        <div className="teams-header-text">{t("practices.group_planning.title")}, {t("practices.group_planning.step")} {step}/{5}</div>
        <div className="teams-btn"></div>
      </div>

      <Activity mode={step === 1 ? "visible" : "hidden"}>
        <div>{t("practices.group_planning.step1_description")}</div>
        <GroupsAndTeamsSelect
          onChange={(ids) => setSelectedTeamIds(ids)}
          { ...{ teams, groups, selectedTeamIds } }
        ></GroupsAndTeamsSelect>
      </Activity>

      { step === 2 &&
        <div className="form-content">
          <div>{t("practices.group_planning.step2_description")}</div>
          <div className="form-group w300">
            <label htmlFor="groupName">{t('teams.age')}</label>
            <Select
              styles={ageStyles}
              closeMenuOnSelect={true}
              options={[5,6,7,8,9,10,11,12,13,14,15].map(value => ({ value, label: value}))}
              isSearchable={false}
              onChange={({ value }) => setPractice({...practice, age: value })}
              value={{ value: practice.age, label: practice.age }}
            />
          </div>
          <div className="form-group w300">
            <label htmlFor="groupName">{t('teams.minutes_per_practice')}</label>
            <input
              id="name"
              type="number"
              min={5} max={150}
              value={practice.duration}
              onChange={(e) => setPractice({...practice, duration: e.target.value})}
            />
          </div>
        </div> 
      }

      { step === 3 &&
        <div className="form-content">
          <div className="form-content-description">{t("practices.group_planning.step3_description")}</div>
          <div className="form-group w400">
            {['manual', 'generate', 'from_template'].map((source_method => <div className="radio-button" key={source_method}>
              <label htmlFor={source_method}>{t('practices.add_practice.' + source_method)}</label>
              <input
                id={source_method}
                type="radio"
                name="source_method"
                value={source_method}
                checked={practice.source_method === source_method}
                onChange={() => setPractice({...practice, source_method})}
              />
              <i className='icon pointer icon-check green' onClick={() => setPractice({...practice, source_method})}></i>
            </div>)) }
              
          </div>
        </div> 
      }

      { step === 4 &&
        <div>
          { practice.source_method === 'manual' && <PracticeContentPreview practiceData={practiceDraft} setPracticeData={setPracticeDraft} libraries={['Exercise','Aktion', 'ExerciseCollection']} /> }
          { practice.source_method === 'from_template' &&
            <>
              { !practiceTemplate
               ? <LibraryDialog 
                  onChange={(_, item) => setPracticeTemplate(item)}
                  libraries={['PracticeTemplates']}
                  close={() => !practiceTemplate && setStep(step - 1)}
                /> 
                : <div className="library-list-container card-view">
                   <PracticeCardItem practice={practiceTemplate} select={() => setPracticeTemplate(null)}/>
                </div> }
            </> 
          }
        </div> 
      } 

      { step === 5 &&
        <GroupPlaningScheduleList {...{ practices, back }}/>
      }

      <div className="teams-list margin-top">
        { step <= 1 && <button className="button" onClick={() => navigate('/club_admin/club')}>{t("buttons.common.cancel")}</button> }
        { step > 1 && step < 5 && <button className="button" onClick={back}>{t("buttons.common.back")}</button> }
        { step < 5 && <button className="button button-primary" disabled={step == 1 && selectedTeamIds.length == 0} onClick={next}>{t("buttons.common.next")}</button> }
      </div>

    </div>
  );
};

export default GroupPracticePlaning;



const GroupsAndTeamsSelect = ({ teams, groups, selectedTeamIds, onChange }) => {
  const { t } = useTranslation();

  const [selectedGroups, setSelectedGroups] = useState([]);

  const teamClick = (team) => {
    if (selectedTeamIds.includes(team.id)) {
      setSelectedGroups(selectedGroups.filter(group_id => group_id !== team.team_group_id))
      onChange?.(selectedTeamIds.filter(id => id !== team.id))
    } else {
      onChange?.([...selectedTeamIds, team.id])
    }
  }

  const groupClick = (group) => {
    const group_teams_ids = teams.filter(team => team.team_group_id === group.id).map(t => t.id)

    if (selectedGroups.includes(group.id)) {
      setSelectedGroups(selectedGroups.filter(id => id !== group.id))
      onChange?.(selectedTeamIds.filter(id => !group_teams_ids.includes(id)))
    } else {
      setSelectedGroups([...selectedGroups, group.id])
      onChange?.([...selectedTeamIds, ...group_teams_ids.filter(id => !selectedTeamIds.includes(id))])
    }
  }

    return <div>
      <div className="teams-list-category">
          <div className="teams-left-column">
            <div className="teams-list-category-left">
              <div className="group-teams">
                <div className="group-teams-item header">
                  <div className="group-icon"></div>
                  <div className="group-name">{t("common.table_name")}</div>
                  <div className="group-coach">{t("common.teams")}</div>
                  <div className="group-actions"></div>
                </div>

                {groups.map((group) => (
                  <div className={`group-teams-item`} key={group.id}>
                    <div className="group-icon"></div>
                    <div className="group-name ellipsis">{group.name}</div>
                    <div className="group-coach">{group.teams_count}</div>
                    <div className="group-actions">
                      <i className={`icon pointer ${ selectedGroups.includes(group.id) ? 'icon-check green' : 'icon-plus'}`} onClick={() => groupClick(group)}></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        <div className="teams-right-column">
          <div className="teams-list-category-left">
            <div className="group-teams">
              <div className="group-teams-item header">
                <div className="group-icon"></div>
                <div className="group-name">{t("common.table_name")}</div>
                <div className="group-age">{t("teams.age")}</div>
                <div className="group-coach">{t("common.members")}</div>
                <div className="group-actions"></div>
              </div>

              {teams.map((team) => (
                <div className={`group-teams-item`} key={team.id}>
                  <div className="group-icon"></div>
                  <div className="group-name ellipsis">{team.name}</div>
                  <div className="group-age">{team.age}</div>
                  <div className="group-coach">{team.users_count}</div>
                  <div className="group-actions">
                    <i className={`icon pointer ${ selectedTeamIds.includes(team.id) ? 'icon-check green' : 'icon-plus'}`} onClick={() => teamClick(team)}></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>;
  
}

const GroupPlaningScheduleList = ({ practices, back }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const SAVE_STATUS = { 'SUCCESS': 'SUCCESS', 'ERROR': 'ERROR', 'NONE': null }

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [status, setStatus] = useState(null)

  useEffect(() => {
    setData([...practices])
  }, [practices])

  const remove = (team_id) => {
    setData(data.filter(entry => entry.team_id !== team_id ))
  }

  const changeSchedule = (field, {team_id}, value) => {
    const modified = [...data];

    const index = modified.findIndex(item => item.team_id === team_id)

    if (index === -1) {
      return;
    }

    modified[index][field] = value;
    setData(modified);
  };

  const handleSaveDates = (date) => {
    const { team_id } = editingEntry;
    let modified = [...data];
    const index = modified.findIndex(item => item.team_id === team_id)

    if (index === -1) {
      return;
    }

    const currentValue = modified[index];

    modified.splice(index, 1, { ...currentValue, scheduled_at: date })

    setData(modified)
  };

  const save = async () => {
    setLoading(true);
    let performStatus = SAVE_STATUS.NONE;

    for (const item of data.filter(practice => practice?.status !== 'done')) {
      let practice = {...item};
      practice.scheduled_at = dayjs(item.scheduled_at).format('YYYY-MM-DDTHH:mm:ss');

      changeSchedule('status', item, 'inProgress');
      
      try {
        await createPractice(practice);
        changeSchedule('status', item, 'done')
      } catch (error) {
        changeSchedule('status', item, 'error');
        const errorMessage = error?.response?.data?.errors?.join(", ") || 'Error occurred';
        changeSchedule('error', item, errorMessage);
        performStatus = SAVE_STATUS.ERROR;

        console.error('Error creating practice:', error);
      }
    }

    setStatus(performStatus !== SAVE_STATUS.ERROR ? SAVE_STATUS.SUCCESS : SAVE_STATUS.ERROR);
    setLoading(false);
  };


  return(
    <div className="group-date">
      <div className="group-date-header">
        <div className="group-day">{t('schedule.date')}</div>
        <div className="group-hour">{t('schedule.hour')}</div>
        <div className="group-duration">{t('schedule.minutes_per_practice')}</div>
        <div className="group-practice">{t('coaches.table_team')}</div>
        <div className="group-actions"></div>
      </div>
      {data.map((entry) => (
        <div className="group-date-item" key={entry.team_id}>
          <div className="group-day">{dayjs(entry.scheduled_at).format('dddd, D MMM')} <i className="pointer icon icon-calendar"  onClick={() => setEditingEntry(entry)}/></div>
          <div className="group-hour">
            <TimePicker value={entry.scheduled_at} onChange={value => changeSchedule('scheduled_at', entry, value)}/>
          </div>
          <div className="group-duration">
            <input type="number" value={entry?.duration} min={5} max={150} placeholder={entry?.duration} onChange={e => changeSchedule('duration', entry, e.target.value)} />
          </div>
          <div className="group-practice">
            { entry.team.name }
          </div>
          <div className="group-actions">
            { !entry?.status && <i className="icon icon-trash pointer" onClick={() => remove(entry.team_id)}></i> }
            { entry?.status === 'done' && <i className="icon icon-check green"></i> }
            { entry?.status === 'error' &&
              <i className="icon icon-info red tooltip">
                 <span className="tooltiptext">{entry.error}</span>
              </i>
             }
            { entry?.status === 'inProgress' && <i className="icon icon-hourglass yellow" /> }
          </div>
        </div>
      ))}

      <div className="teams-list margin-top group-practice-buttons">
        { !status && <button className="button" disabled={loading} onClick={back}>{t("buttons.common.back")}</button> }
        { (!status || status === SAVE_STATUS.ERROR) && <button className="button button-primary" disabled={loading} onClick={save}>{t("buttons.common.save")}</button> }
        { [SAVE_STATUS.ERROR, SAVE_STATUS.SUCCESS].includes(status) && <button className="button button-clone" disabled={loading} onClick={() => navigate('/club_admin/club')}>{t("common.done")}</button>}
      </div>


      {editingEntry && (
        <DatePickerDialog
          close={() => setEditingEntry(null)}
          onChange={handleSaveDates}
          value={editingEntry?.scheduled_at}
        />
      )}
    </div>  
    )
  }

// ToDo: Remove after upgrade to react 19.2 version
function Activity({ mode, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    // Hide without unmounting
    ref.current.style.display = mode === "visible" ? "" : "none";
  }, [mode]);

  return <div ref={ref}>{children}</div>;
}

